import { useEffect, useState, useRef } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { timeAgo } from "@/lib/utils.jobs";
import {
  Briefcase, CheckCircle, FileXls, Users, Plus, PencilSimple, Trash,
  UploadSimple, Code, ArrowClockwise, Tray, DownloadSimple, LinkSimple,
  Envelope, Phone, ArrowClockwise as Spinner,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
const SAMPLE_XML = `<?xml version="1.0" encoding="UTF-8"?>
<jobs>
  <job>
    <reference>VN-1001</reference>
    <title>Senior Frontend Developer</title>
    <company>Stafford Tech</company>
    <location>London, UK</location>
    <category>IT</category>
    <jobtype>Full-time</jobtype>
    <salary>£55,000 - £70,000</salary>
    <description>We are looking for a skilled frontend developer...</description>
    <applyurl>https://example.com/apply/1001</applyurl>
  </job>
</jobs>`;

const EMPTY_JOB = {
  title: "", company: "", location: "", country: "UK", category: "", job_type: "",
  salary: "", description: "", apply_url: "", apply_email: "", reference: "",
};

function StatCard({ icon: Icon, color, value, label }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
        <Icon size={22} weight="fill" />
      </span>
      <div>
        <div className="font-heading text-xl font-bold leading-none">{value}</div>
        <div className="mt-1 text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total_jobs: "—", active_jobs: "—", uk_jobs: "—", us_jobs: "—", applications: "—", new_applications: 0, enquiries: 0, new_enquiries: 0 });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  const loadJobs = () => {
    setLoading(true);
    api.get("/admin/jobs").then(({ data }) => setJobs(data.jobs)).finally(() => setLoading(false));
  };

  useEffect(() => { loadStats(); loadJobs(); }, []);

  return (
    <div className="App bg-secondary/40 min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-10 md:px-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="admin-heading">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage vacancies and import jobs via XML feed.</p>

        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5">
          <StatCard icon={Briefcase} color="text-blue-600 bg-blue-50" value={stats.total_jobs} label="Total Jobs" />
          <StatCard icon={CheckCircle} color="text-emerald-600 bg-emerald-50" value={stats.active_jobs} label="Active Jobs" />
          <StatCard icon={Briefcase} color="text-indigo-600 bg-indigo-50" value={stats.uk_jobs} label="UK Jobs" />
          <StatCard icon={Briefcase} color="text-rose-600 bg-rose-50" value={stats.us_jobs} label="US Jobs" />
          <StatCard icon={Tray} color="text-orange-500 bg-orange-50" value={stats.applications} label="Applications" />
        </div>

        <Tabs defaultValue="jobs" className="mt-8">
          <TabsList className="h-auto flex-wrap justify-start">
            <TabsTrigger value="jobs" data-testid="tab-manage-jobs">Manage Jobs</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">
              Applications{stats.new_applications > 0 ? ` (${stats.new_applications})` : ""}
            </TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">
              Enquiries{stats.new_enquiries > 0 ? ` (${stats.new_enquiries})` : ""}
            </TabsTrigger>
            <TabsTrigger value="feed" data-testid="tab-feed-import">Feed URL</TabsTrigger>
            <TabsTrigger value="xml" data-testid="tab-xml-import">Paste XML</TabsTrigger>
            <TabsTrigger value="csv" data-testid="tab-csv-export">CSV Export</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="mt-5">
            <JobsManager jobs={jobs} loading={loading} reload={() => { loadJobs(); loadStats(); }} />
          </TabsContent>

          <TabsContent value="applications" className="mt-5">
            <ApplicationsManager reload={loadStats} />
          </TabsContent>

          <TabsContent value="enquiries" className="mt-5">
            <EnquiriesManager reload={loadStats} />
          </TabsContent>

          <TabsContent value="feed" className="mt-5">
            <FeedImporter reload={() => { loadJobs(); loadStats(); }} />
          </TabsContent>

          <TabsContent value="xml" className="mt-5">
            <XmlImporter reload={() => { loadJobs(); loadStats(); }} />
          </TabsContent>

          <TabsContent value="csv" className="mt-5">
            <CsvExporter />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

function JobForm({ initial, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || EMPTY_JOB);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...EMPTY_JOB, ...form };
    delete payload.id;
    try {
      if (isEdit) await api.put(`/admin/jobs/${initial.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success(isEdit ? "Job updated" : "Job created");
      setOpen(false);
      if (!isEdit) setForm(EMPTY_JOB);
      onSaved();
    } catch {
      toast.error("Failed to save job");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Job" : "Add New Job"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="job-form">
          <div><Label>Title *</Label><Input required value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="job-form-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => set("company", e.target.value)} className="mt-1" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Country</Label>
              <Select value={form.country} onValueChange={(v) => set("country", v)}>
                <SelectTrigger className="mt-1" data-testid="job-form-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1" placeholder="e.g. IT" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Job type</Label><Input value={form.job_type} onChange={(e) => set("job_type", e.target.value)} className="mt-1" placeholder="Full-time" /></div>
            <div><Label>Salary</Label><Input value={form.salary} onChange={(e) => set("salary", e.target.value)} className="mt-1" placeholder="£40,000 - £50,000" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 min-h-24" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Apply URL</Label><Input value={form.apply_url} onChange={(e) => set("apply_url", e.target.value)} className="mt-1" /></div>
            <div><Label>Apply email</Label><Input value={form.apply_email} onChange={(e) => set("apply_email", e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-full" disabled={saving} data-testid="job-form-submit">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create job"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function JobsManager({ jobs, loading, reload }) {
  const toggleStatus = async (id) => {
    try { await api.patch(`/admin/jobs/${id}/status`); reload(); }
    catch { toast.error("Failed to update status"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/jobs/${id}`); toast.success("Job deleted"); reload(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border p-4">
        <h3 className="font-heading text-lg font-bold">All Jobs ({jobs.length})</h3>
        <JobForm
          onSaved={reload}
          trigger={<Button className="rounded-full" data-testid="add-job-btn"><Plus size={18} className="mr-1" /> Add Job</Button>}
        />
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground" data-testid="admin-no-jobs">No jobs yet. Add one or import via XML.</div>
      ) : (
        <div className="divide-y divide-border">
          {jobs.map((job) => (
            <div key={job.id} className="flex items-center justify-between gap-4 p-4" data-testid={`admin-job-row-${job.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="truncate font-semibold">{job.title}</p>
                  <Badge variant={job.status === "active" ? "default" : "secondary"} className="shrink-0">
                    {job.status}
                  </Badge>
                  {job.source === "xml" && <Badge variant="outline" className="shrink-0 text-accent">XML</Badge>}
                  <Badge variant="outline" className="shrink-0">{job.country || "UK"}</Badge>
                </div>
                <p className="truncate text-sm text-muted-foreground">
                  {job.company || "—"} · {job.location || "—"} · {timeAgo(job.created_at)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" className="rounded-full" onClick={() => toggleStatus(job.id)} data-testid={`toggle-status-${job.id}`}>
                  {job.status === "active" ? "Deactivate" : "Activate"}
                </Button>
                <JobForm
                  initial={job}
                  onSaved={reload}
                  trigger={<Button variant="ghost" size="icon" className="rounded-full" data-testid={`edit-job-${job.id}`}><PencilSimple size={18} /></Button>}
                />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full text-destructive hover:text-destructive" data-testid={`delete-job-${job.id}`}><Trash size={18} /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this job?</AlertDialogTitle>
                      <AlertDialogDescription>"{job.title}" will be permanently removed. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={() => remove(job.id)} data-testid={`confirm-delete-${job.id}`}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function XmlImporter({ reload }) {
  const [xml, setXml] = useState("");
  const [deactivate, setDeactivate] = useState(false);
  const [country, setCountry] = useState("UK");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setXml(String(reader.result)); toast.success(`Loaded ${file.name}`); };
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!xml.trim()) return toast.error("Paste or upload XML first");
    setImporting(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/import/xml", { xml, deactivate_missing: deactivate, default_country: country });
      setResult(data);
      toast.success(`Imported ${data.imported}, updated ${data.updated}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold"><Code size={20} /> XML Feed Import</h3>
          <button className="text-xs font-medium text-primary hover:underline" onClick={() => setXml(SAMPLE_XML)} data-testid="load-sample-xml">
            Load sample
          </button>
        </div>

        <label
          className="mb-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-secondary/40 py-6 text-center transition-colors hover:border-primary"
          data-testid="xml-dropzone"
        >
          <UploadSimple size={26} className="text-muted-foreground" />
          <span className="text-sm font-medium">Upload an XML file</span>
          <span className="text-xs text-muted-foreground">or paste the feed content below</span>
          <input ref={fileRef} type="file" accept=".xml,text/xml,application/xml" className="hidden" onChange={onFile} data-testid="xml-file-input" />
        </label>

        <Textarea
          value={xml}
          onChange={(e) => setXml(e.target.value)}
          placeholder="Paste your XML feed here…"
          className="min-h-56 font-mono text-xs"
          data-testid="xml-textarea"
        />

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Feed country</span>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-9 w-44" data-testid="import-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UK">United Kingdom</SelectItem>
                <SelectItem value="US">United States</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="text-xs text-muted-foreground">Used when a job has no country tag of its own.</span>
        </div>

        <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-sm">
          <Checkbox checked={deactivate} onCheckedChange={setDeactivate} data-testid="deactivate-missing-check" />
          Deactivate {country} XML jobs not present in this feed (full sync)
        </label>

        <Button className="mt-4 rounded-full" onClick={runImport} disabled={importing} data-testid="run-import-btn">
          {importing ? <><ArrowClockwise size={18} className="mr-1 animate-spin" /> Importing…</> : <><UploadSimple size={18} className="mr-1" /> Import Jobs</>}
        </Button>
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h4 className="font-heading font-bold">How it works</h4>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>• Flexible parser reads common tags: title, company, location, salary, etc.</li>
            <li>• Jobs with a matching <code className="text-accent">reference</code> are updated, not duplicated.</li>
            <li>• Namespaces and mixed field names are handled automatically.</li>
          </ul>
        </div>

        {result && (
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="import-result">
            <h4 className="font-heading font-bold">Import Result</h4>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-secondary p-3"><div className="font-heading text-xl font-bold">{result.total_found}</div><div className="text-xs text-muted-foreground">Found</div></div>
              <div className="rounded-lg bg-emerald-50 p-3"><div className="font-heading text-xl font-bold text-emerald-600">{result.imported}</div><div className="text-xs text-muted-foreground">Imported</div></div>
              <div className="rounded-lg bg-blue-50 p-3"><div className="font-heading text-xl font-bold text-blue-600">{result.updated}</div><div className="text-xs text-muted-foreground">Updated</div></div>
              <div className="rounded-lg bg-orange-50 p-3"><div className="font-heading text-xl font-bold text-orange-500">{result.deactivated}</div><div className="text-xs text-muted-foreground">Deactivated</div></div>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-3 max-h-32 overflow-y-auto rounded-lg bg-destructive/5 p-3 text-xs text-destructive">
                {result.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const STATUS_OPTS = ["new", "reviewed", "shortlisted", "rejected"];
const STATUS_STYLE = {
  new: "bg-orange-100 text-orange-700",
  reviewed: "bg-blue-100 text-blue-700",
  shortlisted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700",
};

function ApplicationsManager({ reload }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/admin/applications", { params: filter !== "all" ? { status: filter } : {} })
      .then(({ data }) => setApps(data.applications))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}/status`, null, { params: { status } });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      reload?.();
    } catch { toast.error("Could not update status"); }
  };

  const viewCv = (id) => {
    window.open(`${API}/admin/applications/${id}/cv`, "_blank", "noopener");
  };

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <h3 className="font-heading text-lg font-bold">Applications ({apps.length})</h3>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-40" data-testid="applications-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : apps.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground" data-testid="no-applications">No applications yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {apps.map((a) => (
            <div key={a.id} className="p-4" data-testid={`application-row-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{a.name}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLE[a.status] || "bg-secondary"}`}>
                      {a.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Applied for <span className="font-medium text-foreground">{a.job_title}</span>
                    {a.job_company ? <span className="text-muted-foreground"> · Employer: {a.job_company}</span> : null}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Envelope size={13} /> {a.email}</span>
                    {a.phone && <span className="flex items-center gap-1"><Phone size={13} /> {a.phone}</span>}
                    <span>{new Date(a.created_at).toLocaleDateString()}</span>
                  </div>
                  {a.cover_letter && (
                    <p className="mt-2 max-w-2xl rounded-lg bg-secondary/60 p-2 text-xs text-foreground/80">{a.cover_letter}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => viewCv(a.id)} data-testid={`view-cv-${a.id}`}>
                    <DownloadSimple size={16} className="mr-1" /> CV
                  </Button>
                  <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                    <SelectTrigger className="h-9 w-36 capitalize" data-testid={`status-select-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CR_BASE = "https://collegerecruiter-production-feeds.s3.us-west-2.amazonaws.com/publisher-feeds";
const FEEDS = {
  UK_CPA: `${CR_BASE}/265a24ac2d874d9088bbb15e070608b5.xml.gz`,
  UK_CPC: `${CR_BASE}/e40519c07abb4b2e9e83b416ddc1ad17.xml.gz`,
  US_CPA: `${CR_BASE}/3fc9f72521174368b9433025b50edf8a.xml.gz`,
  US_CPC: `${CR_BASE}/bc0eb256cc0a4b22bd92e024a8b1e069.xml.gz`,
};

function FeedImporter({ reload }) {
  const [url, setUrl] = useState("");
  const [country, setCountry] = useState("UK");
  const [deactivate, setDeactivate] = useState(true);
  const [clearSamples, setClearSamples] = useState(true);
  const [importingAll, setImportingAll] = useState(false);
  const [starting, setStarting] = useState(false);
  const [imports, setImports] = useState([]);

  const loadStatus = () => api.get("/admin/import/feed/status").then(({ data }) => setImports(data.imports)).catch(() => {});
  useEffect(() => {
    loadStatus();
    const t = setInterval(() => { loadStatus(); reload?.(); }, 4000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    if (!url.trim()) return toast.error("Enter a feed URL");
    setStarting(true);
    try {
      await api.post("/admin/import/feed", { url, default_country: country, deactivate_missing: deactivate });
      toast.success(`${country} feed import started`);
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start import");
    } finally {
      setStarting(false);
    }
  };

  const importAll = async () => {
    setImportingAll(true);
    try {
      const { data } = await api.post("/admin/import/all-feeds", null, { params: { clear_samples: clearSamples } });
      toast.success(`Started ${data.started.length} feed import(s)`);
      loadStatus();
      reload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start imports");
    } finally {
      setImportingAll(false);
    }
  };

  const reactivate = async () => {
    try {
      const { data } = await api.post("/admin/jobs/reactivate");
      toast.success(`Reactivated ${data.reactivated} job(s)`);
      reload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reactivate");
    }
  };

  const running = imports.some((i) => i.status === "running");

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold"><LinkSimple size={20} /> Import from feed URL</h3>
        <p className="mt-1 text-sm text-muted-foreground">Paste a feed URL (.xml or gzipped .xml.gz). We fetch, decompress and import in the background.</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <button className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary" onClick={() => { setUrl(FEEDS.UK_CPA); setCountry("UK"); }} data-testid="preset-uk-cpa-feed">UK CPA</button>
          <button className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary" onClick={() => { setUrl(FEEDS.UK_CPC); setCountry("UK"); }} data-testid="preset-uk-cpc-feed">UK CPC</button>
          <button className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary" onClick={() => { setUrl(FEEDS.US_CPA); setCountry("US"); }} data-testid="preset-us-cpa-feed">US CPA</button>
          <button className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary" onClick={() => { setUrl(FEEDS.US_CPC); setCountry("US"); }} data-testid="preset-us-cpc-feed">US CPC</button>
        </div>

        <div className="mt-4 rounded-xl bg-primary/5 p-4">
          <p className="text-sm font-medium">Populate everything in one click</p>
          <p className="mt-1 text-xs text-muted-foreground">Imports both the UK and US feeds. Tick below to also remove any sample/demo jobs first (recommended on a new site).</p>
          <label className="mt-3 flex cursor-pointer items-center gap-2.5 text-sm">
            <Checkbox checked={clearSamples} onCheckedChange={setClearSamples} data-testid="clear-samples-check" />
            Remove sample/demo jobs first
          </label>
          <Button className="mt-3 rounded-full" variant="secondary" onClick={importAll} disabled={importingAll} data-testid="import-all-feeds-btn">
            <UploadSimple size={18} className="mr-1" /> Import all feeds (UK + US)
          </Button>
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">Bring back any previously-hidden jobs (e.g. the ones lost to an empty feed). Jobs no longer auto-hide when they drop out of a feed — applications happen on-site, so there's no expired-redirect risk.</p>
            <Button className="mt-2 rounded-full" variant="outline" size="sm" onClick={reactivate} data-testid="reactivate-jobs-btn">
              <ArrowClockwise size={16} className="mr-1" /> Reactivate hidden feed jobs
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <Label>Feed URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/feed.xml.gz" className="mt-1 font-mono text-xs" data-testid="feed-url-input" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Feed country</span>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger className="h-9 w-44" data-testid="feed-country-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="UK">United Kingdom</SelectItem>
                <SelectItem value="US">United States</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <label className="mt-4 flex cursor-pointer items-center gap-2.5 text-sm">
          <Checkbox checked={deactivate} onCheckedChange={setDeactivate} data-testid="feed-deactivate-check" />
          Apply freshness cap — hide {country} jobs posted over 60 days ago
        </label>

        <Button className="mt-4 rounded-full" onClick={start} disabled={starting || running} data-testid="start-feed-import-btn">
          {starting ? <><Spinner size={18} className="mr-1 animate-spin" /> Starting…</> : running ? "Import running…" : <><UploadSimple size={18} className="mr-1" /> Start Import</>}
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h4 className="font-heading font-bold">Recent imports</h4>
        <div className="mt-3 space-y-3" data-testid="feed-import-list">
          {imports.length === 0 && <p className="text-sm text-muted-foreground">No imports yet.</p>}
          {imports.map((i) => (
            <div key={i.import_id} className="rounded-lg border border-border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{i.country}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  i.status === "done" ? "bg-emerald-100 text-emerald-700" :
                  i.status === "running" ? "bg-blue-100 text-blue-700" : "bg-rose-100 text-rose-700"}`}>
                  {i.status}
                </span>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {i.status === "running" ? `Processed ${i.processed || 0} jobs…` :
                 i.status === "done" ? `${i.total} imported · ${i.deactivated || 0} deactivated` :
                 (i.error || "failed")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function CsvExporter() {
  const [url, setUrl] = useState("");
  const [country, setCountry] = useState("UK");
  const [starting, setStarting] = useState(false);
  const [exports, setExports] = useState([]);

  const load = () => api.get("/admin/export/csv").then(({ data }) => setExports(data.exports)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    if (!url.trim()) return toast.error("Paste an XML feed URL");
    setStarting(true);
    try {
      await api.post("/admin/export/csv", { url: url.trim(), country });
      toast.success("CSV export started — this can take a few minutes for large feeds");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start export");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold"><FileXls size={20} /> Feed to CSV</h3>
        <p className="mt-1 text-sm text-muted-foreground">Paste an XML feed URL. We scan the whole feed and build a CSV of the top 50,000 jobs by CPA (highest first) — ready for Google Sheets.</p>
        <p className="mt-2 text-xs text-muted-foreground">Columns: External URL · CPA Amount · Job Location · Job Title · Vacancy Network URL</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary" onClick={() => { setUrl(FEEDS.UK_CPA); setCountry("UK"); }} data-testid="csv-preset-uk-cpa">UK CPA</button>
          <button className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary" onClick={() => { setUrl(FEEDS.US_CPA); setCountry("US"); }} data-testid="csv-preset-us-cpa">US CPA</button>
        </div>
        <div className="mt-4">
          <Label>XML feed URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/feed.xml.gz" className="mt-1 font-mono text-xs" data-testid="csv-feed-url-input" />
        </div>
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm font-medium">Feed country</span>
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="h-9 w-44" data-testid="csv-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="UK">United Kingdom</SelectItem>
              <SelectItem value="US">United States</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button className="mt-4 rounded-full" onClick={start} disabled={starting} data-testid="start-csv-export-btn">
          {starting ? <><Spinner size={18} className="mr-1 animate-spin" /> Starting…</> : <><DownloadSimple size={18} className="mr-1" /> Build CSV</>}
        </Button>
      </div>
      <div className="rounded-2xl border border-border bg-card p-5">
        <h4 className="font-heading font-bold">Recent exports</h4>
        <div className="mt-3 space-y-3" data-testid="csv-export-list">
          {exports.length === 0 && <p className="text-sm text-muted-foreground">No exports yet.</p>}
          {exports.map((e) => <CsvExportRow key={e.export_id} e={e} />)}
        </div>
      </div>
    </div>
  );
}

function CsvExportRow({ e }) {
  const tone = e.status === "done" ? "bg-emerald-100 text-emerald-700" : e.status === "running" ? "bg-blue-100 text-blue-700" : "bg-rose-100 text-rose-700";
  return (
    <div className="rounded-lg border border-border p-3 text-sm" data-testid={`csv-export-${e.export_id}`}>
      <div className="flex items-center justify-between">
        <span className="font-semibold">{e.country} <span className="text-xs font-normal text-muted-foreground" data-testid={`csv-export-time-${e.export_id}`}>{new Date(e.finished_at || e.started_at).toLocaleString()}</span></span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{e.status}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {e.status === "running" ? `Scanned ${e.scanned || 0} jobs…` :
         e.status === "done" ? `${e.rows} rows · ${e.matched} linked to site · ${e.scanned} scanned` : (e.error || "failed")}
      </div>
      {e.status === "done" && (
        <Button size="sm" variant="outline" className="mt-2 rounded-full" onClick={() => window.open(`${API}/admin/export/csv/${e.export_id}/download`, "_blank", "noopener")} data-testid={`download-csv-${e.export_id}`}>
          <DownloadSimple size={16} className="mr-1" /> Download CSV
        </Button>
      )}
    </div>
  );
}

function EnquiriesManager({ reload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/enquiries").then(({ data }) => setItems(data.enquiries)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}/status`, null, { params: { status } });
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      reload?.();
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="border-b border-border p-4">
        <h3 className="font-heading text-lg font-bold">Employer Enquiries ({items.length})</h3>
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-sm text-muted-foreground" data-testid="no-enquiries">No enquiries yet.</div>
      ) : (
        <div className="divide-y divide-border">
          {items.map((e) => (
            <div key={e.id} className="p-4" data-testid={`enquiry-row-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{e.name}{e.company ? ` · ${e.company}` : ""}</p>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium capitalize text-primary">
                      {e.enquiry_type === "advertise" ? "Advertise" : "General"}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${e.status === "new" ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {e.status}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Envelope size={13} /> {e.email}</span>
                    {e.phone && <span className="flex items-center gap-1"><Phone size={13} /> {e.phone}</span>}
                    <span>{new Date(e.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="mt-2 max-w-2xl whitespace-pre-wrap rounded-lg bg-secondary/60 p-2 text-sm text-foreground/80">{e.message}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <a href={`mailto:${e.email}`} className="text-sm font-medium text-primary hover:underline" data-testid={`reply-${e.id}`}>Reply</a>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => setStatus(e.id, e.status === "new" ? "handled" : "new")} data-testid={`toggle-enquiry-${e.id}`}>
                    {e.status === "new" ? "Mark handled" : "Reopen"}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

